import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import { VenueCard, cardGrid } from "../components/Cards";
import { Pagination } from "../components/Pagination";
import { LetterBar } from "../components/LetterBar";
import { useSettings } from "../context/SettingsContext";
import { useTerms } from "../lib/useTerms";

const STATES = ["", "Florida", "Alabama", "Mississippi", "Louisiana", "Texas"];
const VTYPES = ["Live music", "Bar", "Restaurant", "Beach venue", "Theater", "Festival space", "Event hall"];

export default function Venues() {
  const { settings } = useSettings();
  const cs = settings?.card_styles?.venues || "default";
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: sp.get("q") || "", state: "", city: "", venue_type: "", letter: "" });
  const vtypes = useTerms("venue", "venue_type", VTYPES);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 12 });
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    const { data } = await api.get(`/venues?${p.toString()}`);
    setItems(data.items); setMeta(data); setLoading(false);
  }, [f, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };
  const sel = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-rose-500";

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10" data-testid="venues-page">
      <p className="overline mb-2">Stages, bars & beach clubs</p>
      <h1 className="font-display font-extrabold text-4xl md:text-5xl mb-8">Venue Directory</h1>
      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-20 bg-[#111] border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 font-semibold"><SlidersHorizontal size={16} /> Filters</div>
            <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search venues" className={sel} data-testid="vfilter-q" />
            <select value={f.state} onChange={(e) => set("state", e.target.value)} className={sel} data-testid="vfilter-state">{STATES.map((s) => <option key={s} value={s}>{s || "All States"}</option>)}</select>
            <input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className={sel} data-testid="vfilter-city" />
            <select value={f.venue_type} onChange={(e) => set("venue_type", e.target.value)} className={sel} data-testid="vfilter-type"><option value="">All Types</option>{vtypes.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </div>
        </aside>
        <div className="lg:col-span-9 min-w-0">
          <LetterBar value={f.letter} onChange={(v) => set("letter", v)} />
          <p className="text-sm text-zinc-400 mb-5" data-testid="venues-count">{loading ? "Loading..." : `Showing ${items.length} of ${meta.total} venue${meta.total === 1 ? "" : "s"}${f.letter ? ` under ${f.letter.toUpperCase()}` : ""}`}</p>
          <div className={`grid gap-5 ${cardGrid(cs, "sm:grid-cols-2")}`}>{items.map((v) => <VenueCard key={v.id} v={v} style={cs} />)}</div>
          {!loading && items.length === 0 && (
            <div className="text-center py-20 space-y-3" data-testid="venues-empty">
              <p className="text-zinc-300 font-semibold">No venues found{f.letter ? ` under ${f.letter.toUpperCase()}` : ""}.</p>
              <p className="text-zinc-500 text-sm">Try clearing your filters or browse all venues — or claim and submit your venue!</p>
              <button onClick={() => { setF({ q: "", state: "", city: "", venue_type: "", letter: "" }); setPage(1); }}
                className="text-sm bg-rose-500 hover:bg-rose-600 font-bold px-5 py-2 rounded-lg" data-testid="venues-clear-filters">Clear all filters</button>
            </div>
          )}
          <Pagination page={page} pages={meta.pages} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}
